<template>
  <div ref="streamsList" class="audio-list">
    <div class="audio-item" v-for="item in streamsList" :key="item.id" :video="item">
      <video class="audio-player" autoplay ref="audios" :muted="item.isLocal" :id="item.id"></video>
      <div class="user-name">{{item.username}}</div>
      <div class="custom-overlay" @click.prevent="videoClicked($event, item.id)">
        <AudioVisualizer style="position: absolute;top:0;left:40px" :media="item.stream" />
        <img class="header-avatar-image" style="position: absolute;top:0" :src="item.image" v-if="item.image">
      </div>
    </div>
  </div>
</template>

<script>
import io from 'socket.io-client';
import SimpleSignalClient from 'simple-signal-client';
import api from '@/services/api';
import AudioVisualizer from '@/components/webrtc/AudioVisualizer';

export default {
  name: 'BlitzarTalk',
  components: { AudioVisualizer },
  data() {
    return {
      signalClient: null,
      streamsList: [],
      canvas: null,
      socket: null,
      peers: [],
      localStream: null,
      screenStream: null,
      statsOutput: '',
    };
  },
  props: {
    roomId: {
      type: String,
      default: 'public-room',
    },
    userName: {
      type: String,
      default: '',
    },
    userImage: {
      type: String,
      default: '',
    },
    socketURL: {
      type: String,
      // default: 'localhost:3000',
      default: 'https://talk.blitzar.com.br/',
    },
    isVerticalOrientation: {
      type: Boolean,
      default: false,
    },
    autoplay: {
      type: Boolean,
      default: true,
    },
    screenshotFormat: {
      type: String,
      default: 'image/jpeg',
    },
    enableAudio: {
      type: Boolean,
      default: true,
    },
    enableVideo: {
      type: Boolean,
      default: true,
    },
    enableLogs: {
      type: Boolean,
      default: true,
    },
    peerOptions: {
      type: Object, // NOTE: use these options: https://github.com/feross/simple-peer
      default() {
        return {};
      },
    },
    deviceId: {
      type: String,
      default: null,
    },
  },
  watch: {
    streamsList: function (newVal, oldVal) {
      // console.log('watch streamsList', newVal);
      newVal.forEach((item) => {
        if (item.stream.getVideoTracks()[0]) {
          item.stream.getVideoTracks()[0].addEventListener('ended', () => {
            // console.log('stream ended on watch');
          });
        }
      });
    },
  },
  methods: {
    async join() {
      const that = this;
      // console.log('join');
      this.socket = io(this.socketURL, { timeout: 1000, reconnectionDelayMax: 1000, rejectUnauthorized: false, transports: ['websocket'] });
      this.signalClient = new SimpleSignalClient(this.socket, { connectionTimeout: 1000 });
      let constraints = {
        video: false,
        audio: true,
      };
      try {
        this.localStream = await navigator.mediaDevices.getUserMedia(constraints);
        // console.log('getUserMedia stream opened', this.localStream);
        this.joinedRoom(that.localStream, true, JSON.stringify({ username: that.userName, image: that.userImage, isScreen: false }));

        this.signalClient.once('discover', (discoveryData) => {
          // console.log('discovered', { discoveryData });
          that.peers = discoveryData.peers;

          setTimeout(() => {
            that.getStats();
          }, 3000);

          async function connectToPeer(peerID, idx) {
            if (peerID == that.socket.id) return;
            try {
              // console.log('Connecting to peer');
              const { peer } = await that.signalClient.connect(peerID, JSON.stringify({ username: that.userName, image: that.userImage }), that.peerOptions);
              // console.log('connected to', peerID, that.streamsList.length);
              that.streamsList.forEach((v) => {
                if (v.isLocal) {
                  that.onPeer(peer, v.stream, JSON.stringify({ username: that.peers[idx].user_name, image: that.peers[idx].image }));
                }
              });
            } catch (e) {
              that.log('Error connecting to peer');
            }
          }
          discoveryData.peers.forEach((peer, idx) => connectToPeer(peer.user_id, idx));
          that.$emit('opened-room', that.roomId);
        });
        this.signalClient.on('request', async (request) => {
          // console.log('requested', { request });
          const { peer } = await request.accept({}, that.peerOptions);
          // console.log('accepted', request.metadata);
          that.streamsList.forEach((v) => {
            if (v.isLocal) {
              that.onPeer(peer, v.stream, request.metadata);
            }
          });
        });
        this.signalClient.discover(JSON.stringify({ roomId: that.roomId, user_name: that.userName, image: that.userImage }));
      } catch (err) {
        console.error('getUserMedia error', err);
        console.error('getUserMedia error NO CAMERA', err);
        alert('Não foi possível abrir a câmera');
        this.$emit('close-modal');
      }
    },
    onPeer(peer, localStream, metadata) {
      const that = this;
      // console.log('onPeer', metadata);

      peer.addStream(localStream);
      peer.on('stream', (remoteStream) => {
        // console.log('peer on stream:', remoteStream.id, { metadata });
        that.joinedRoom(remoteStream, false, metadata);

        peer.on('close', () => {
          // console.log('peer on close', { peer });
          let newList = [];
          that.streamsList.forEach(function (item) {
            if (item.id !== remoteStream.id) {
              newList.push(item);
            }
          });
          that.streamsList = newList;
          that.$emit('left-room', remoteStream.id);
        });
        peer.on('error', (err) => {
          that.log('peer error ', err);
        });
      });
      peer.on('data', (data) => {
        let id = new TextDecoder('utf-8').decode(data);
        // console.log('peer on data: ', id);
        that.streamsList = that.streamsList.filter((video) => video.id != id);
      });
    },
    joinedRoom(stream, isLocal, metadata) {
      // console.log('META', stream.id, metadata);
      if (typeof metadata === 'string') metadata = JSON.parse(metadata);
      else metadata = {};
      const that = this;
      let found = that.streamsList.find((video) => {
        return video.id === stream.id;
      });
      if (found === undefined) {
        let video = {
          id: stream.id,
          visible: true,
          muted: isLocal,
          stream: stream,
          isLocal: isLocal,
          isScreen: metadata.isScreen ? metadata.isScreen : false,
          username: metadata.username ? metadata.username : '',
          image: metadata.image ? metadata.image : '',
        };

        // that.streamsList.push(video);
        that.streamsList = [...that.streamsList, video];
      }

      this.$nextTick(() => {
        for (let i = 0; i < that.$refs.audios.length; i++) {
          if (that.$refs.audios[i].id === stream.id) {
            that.$refs.audios[i].srcObject = stream;
            break;
          }
        }
        that.$emit('joined-room', that.streamsList);
        // console.log('joined-room streamsList length: ', that.streamsList.length);
      });
    },
    getVideosCount() {
      return this.streamsList.length;
    },
    toggleOrientation(isVertical, width, height) {
      let streamCount = this.streamsList.length;
      if (streamCount <= 1) return { width: width, height: height };
      let tempWidth = width;
      return { width: height, height: tempWidth };
    },
    toggleLocalStreamAudioTrack(enable) {
      this.localStream.getAudioTracks()[0].enabled = enable;
      // console.log('toggleLocalStreamAudioTrack', this.localStream.getAudioTracks()[0].enabled);
    },
    toggleMutePeers() {
      this.streamsList.map((stream) => {
        if (!stream.isLocal) stream.stream.getAudioTracks()[0].enabled = !stream.stream.getAudioTracks()[0].enabled;
      });
    },
    capture() {
      return this.getCanvas().toDataURL(this.screenshotFormat);
    },
    getCanvas() {
      let video = this.$refs.audios[0];
      if (video !== null && !this.ctx) {
        let canvas = document.createElement('canvas');
        canvas.height = video.clientHeight;
        canvas.width = video.clientWidth;
        this.canvas = canvas;
        this.ctx = canvas.getContext('2d');
      }
      const { ctx, canvas } = this;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      return canvas;
    },
    async shareScreen() {
      const that = this;
      if (navigator.mediaDevices == undefined) {
        that.log('Error: https is required to load cameras');
        return;
      }

      try {
        that.screenStream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
        that.signalClient.peers().forEach((p) => that.onPeer(p, that.screenStream, JSON.stringify({ username: 'screen_share', isScreen: true })));
        that.joinedRoom(that.screenStream, true, JSON.stringify({ username: 'screen_share', isScreen: true }));
        that.screenStream.getVideoTracks()[0].onended = () => {
          // console.log('Share ended');

          that.signalClient.peers().forEach((peer) => peer.send(that.screenStream.id));
          that.streamsList = that.streamsList.filter((video) => video.id != that.screenStream.id);
          that.$emit('share-ended', this.screenStream.id);
        };
        that.$emit('share-started', that.screenStream.id);
      } catch (e) {
        that.log('Media error: ' + JSON.stringify(e));
      }
    },
    stopScreenSharing() {
      for (let i = 0; i < this.$refs.audios.length; i++) {
        if (this.$refs.audios[i].id === this.screenStream.id) {
          let tracks = this.$refs.audios[i].srcObject.getTracks();
          tracks.forEach((track) => track.stop());
          this.$refs.audios[i].srcObject = null;
          break;
        }
      }
      this.signalClient.peers().forEach((peer) => peer.send(this.screenStream.id));
      this.streamsList = this.streamsList.filter((video) => video.id != this.screenStream.id);

      this.$emit('share-ended', this.screenStream.id);
    },
    leave() {
      // console.log('ran leave()');
      api.post(`sessionAction/`, {
        action: 'User left talk room',
        properties: JSON.stringify({ room_id: this.roomlId }),
        userid: this.$store.state.user.id,
        sessionid: this.$store.state.user.token,
        event_id: this.$store.state.eventId,
      });
      this.streamsList.forEach((v) => v.stream.getTracks().forEach((t) => t.stop()));
      this.streamsList = [];
      this.signalClient.peers().forEach((peer) => peer.removeAllListeners());
      this.signalClient.destroy();
      this.signalClient = null;
      this.socket.destroy();
      this.socket = null;
    },
    log(message, data) {
      if (this.enableLogs) {
        console.log(message);
        if (data != null) {
          console.log(data);
        }
      }
    },
    videoClicked(e, id) {
      // console.log('videoClicked', { e }, id);
    },
    getStats() {
      // this.signalClient
      //   .peers()[0]
      //   ._pc.getStats(this.signalClient.peers()[0]._remoteTracks[0].track)
      //   .then((stats) => {
      //     stats.forEach((report) => {
      //       this.statsOutput += `<h2>Report: ${report.type}</h2>\n<strong>ID:</strong> ${report.id}<br>\n` + `<strong>Timestamp:</strong> ${report.timestamp}<br>\n`;
      //       // Now the statistics for this report; we intentionally drop the ones we
      //       // sorted to the top above
      //       Object.keys(report).forEach((statName) => {
      //         if (statName !== 'id' && statName !== 'timestamp' && statName !== 'type') {
      //           this.statsOutput += `<strong>${statName}:</strong> ${report[statName]}<br>\n`;
      //         }
      //       });
      //     });
      //     console.log('stats', this.statsOutput);
      //   });
    },
  },
  mounted() {
    // this.$store.commit('setUserInMeeting', true);
    document.activeElement.blur();
    this.$refs.streamsList.focus();
    // console.log('sessionAction', this.roomId);
    api.post(`sessionAction/`, {
      action: 'User joined talk room',
      properties: JSON.stringify({ room_id: this.roomId }),
      userid: this.$store.state.user.id,
      sessionid: this.$store.state.user.token,
      event_id: this.$store.state.eventId,
    });
    this.join();
  },
  beforeDestroy() {
    // this.$store.commit('setUserInMeeting', false);
    this.leave();
  },
};
</script>
<style lang="scss" scoped>
.audio-list {
  background: transparent;
  width: 100%;
  max-height: calc(100vw * 9 / 16 - 150px);
  overflow-y: auto;
  padding-right: 4px;

  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;

  @include media("<=phone-plus") {
    max-width: 100px;
  }
}

.audio-item {
  position: relative;
  display: flex;
  justify-content: center;
  margin: 4px;

  .audio-player {
    background: transparent;
    height: 30px;
    width: 260px;
    background: #777;

    @include media("<=phone-plus") {
      width: 160px;
    }
  }

  video::-webkit-media-controls-panel {
    background: #777;
    padding: 0;
  }
  video::-webkit-media-controls-current-time-display,
  video::-webkit-media-controls-time-remaining-display {
    display: none;
  }
}
.audio-item video {
  // min-height: 180px;
  // transform: scaleX(-1);
  border-radius: 14px;
  // &:active {
  //   pointer-events: none;
  // }
}
// ::-webkit-media-controls {
//   transform: scaleX(-1) !important;
// }
video::-webkit-media-controls-play-button,
video::-webkit-media-controls-pausebutton {
  display: none;
}

audio::-webkit-media-controls-timeline,
video::-webkit-media-controls-timeline {
  display: none;
}
.user-name {
  pointer-events: none;
  position: absolute;
  top: 2px;
  right: 6px;
  padding: 4px;
  background: #2229;
  color: #dddd;
  font-size: 16px;
  @include media("<=phone") {
    font-size: 14px;
  }
  border-radius: 4px;
  cursor: pointer;
  user-select: none;
}

.custom-overlay {
  position: absolute;
  inset: 0 0 50px 0;
  background: transparent;
  user-select: auto;

  @include media("<=phone") {
    inset: 0;
  }
}
</style>
